import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { AppState } from '@nxsys/store';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetOnHoldListParams } from '@nxsys/store/slices/dashboard/types';
import { TimeSheetDataItem } from '@nxsys/types/models/timesheet';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export enum OnHoldModalTypeEnum {
  PENDING_INV_GENERATION = 'PENDING_INV_GENERATION',
  PENDING_PAYMENTS = 'PENDING_PAYMENTS',
}

type Props = {
  isOpen: boolean;
  toggle: () => void;
  type: OnHoldModalTypeEnum;
};

const OnHoldModal: React.FC<Props> = ({ isOpen, toggle, type }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const { onHoldList } = useSelector((state: AppState) => state.dashboardStore);
  const { list, pagination, loading, queryParams } = onHoldList;

  useEffect(() => {
    if (isOpen)
      handleFetchData({
        page: 0,
        size: 20,
      });
  }, [isOpen]);

  const handleFetchData = (params: GetOnHoldListParams) => {
    const newParams = {
      ...params,
    };
    if (type === OnHoldModalTypeEnum.PENDING_INV_GENERATION) {
      newParams.isPendingGenerationOnly = true;
    } else if (type === OnHoldModalTypeEnum.PENDING_PAYMENTS) {
      newParams.isPendingSubcontractorPaymentOnly = true;
    }
    dispatch(dashboardActions.getOnHoldList(newParams));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const columns: ColumnsType<TimeSheetDataItem> = [
    {
      key: 'subcontractorRef',
      title: 'Agency Ref',
      dataIndex: 'subcontractorRef',
      render: (value) =>
        `${value?.primaryAgency?.agencyPrefix || '___'}/${value?.agencyRef || value?.systemId || '_'
        }`,
      width: 240,
    },
    {
      key: 'id',
      title: 'Timesheet ID',
      dataIndex: 'id',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 240,
      render: (value, record) => (
        <span>
          {value || '_'}
          {record?.isPAL && (
            <Tag color="warning" className="ml-1">PAL</Tag>
          )}
        </span>
      ),
    },
    {
      key: 'subcontractorRef',
      title: 'Employee ID',
      dataIndex: 'subcontractorRef',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 240,
      render: (props) => <span>{props?.id}</span>,
    },
    {
      key: 'batch',
      title: 'Batch #',
      dataIndex: 'batch',
      render: (props) => <span>{props?.name}</span>,
    },
    {
      key: 'invoiceNo',
      title: 'Inv No',
      dataIndex: 'invoiceNo',
      width: 240,
    },
    {
      key: 'invDate',
      title: 'Inv Date',
      dataIndex: 'invoicedDate',
      align: 'right',
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
    },
    {
      key: 'subcontractorRef',
      title: 'Name',
      dataIndex: 'subcontractorRef',
      render: (props) => <span>{props?.fullName || ''}</span>,
    },
    {
      key: 'agencyRef',
      title: 'Agency Name',
      dataIndex: 'agencyRef',
      width: 350,
      render: (props) => <span>{props?.name}</span>,
    },
    {
      key: 'endDate',
      title: 'Week End Date',
      dataIndex: 'endDate',
      align: 'right',
      sorter: true,
      width: 160,
      sortDirections: ['descend', 'ascend'],
      render: (props) => (
        <span>
          {props ? moment(props).tz('Europe/London').format('DD/MM/YYYY') : ''}
        </span>
      ),
    },
    {
      key: 'taxPeriod',
      title: 'Tax Period',
      // dataIndex: 'taxPeriod',
      render: (record) => (
        <span>{`${record?.taxPeriod || ''}/${record?.taxYear || record?.currentYear || ''
          }`}</span>
      ),
      align: 'right',
    },
    {
      key: 'totalNet',
      title: 'Net',
      dataIndex: 'totalNet',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => (
        <span>{formatMoney(value, showCurrencyByCountry(payrollCountry))}</span>
      ),
    },
    {
      key: 'totalVat',
      title: 'VAT',
      align: 'right',
      sorter: true,
      dataIndex: 'totalVat',
      sortDirections: ['descend', 'ascend'],
      render: (value) => (
        <span>{formatMoney(value, showCurrencyByCountry(payrollCountry))}</span>
      ),
    },
    {
      key: 'totalPrices',
      title: 'Total',
      dataIndex: 'totalPrices',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => (
        <span>
          {formatMoney(value || '0', showCurrencyByCountry(payrollCountry))}
        </span>
      ),
    },
  ];

  return (
    <AppModalContainer
      title={
        type === OnHoldModalTypeEnum.PENDING_INV_GENERATION
          ? 'Pending INV Generation'
          : messages['common.pendingEmployeePayments']
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
      noPaddingBody
    >
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 2700 }}
          columns={columns}
          onChangePagination={onChangePagination}
        />
      </AppsContent>
    </AppModalContainer>
  );
};

export default OnHoldModal;
